import dayjs from "dayjs";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { formatCpf } from "utils/federal-tax-id";

import { ICargaOperadoraSaved } from "./CargaOperadora.types";

/** A linha já achatada, com o contrato de onde ela veio. */
export type LinhaComContrato = ICargaOperadoraSaved & { numeroContrato: string | null };

type Props = {
  data: LinhaComContrato[];
};

export function CargaOperadoraUploadTable({ data }: Props) {
  /** As datas vêm da resposta como string ISO, não como `Date`. */
  const formatDate = (value?: string | null) => (value ? dayjs(value).format("DD/MM/YYYY") : "-");

  const formatMoney = (value?: number | null) =>
    value === null || value === undefined
      ? "-"
      : value.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  const rows = data.map((row, index) => ({
    /** O CPF pode repetir entre contratos, então o índice entra na chave. */
    id: `${row.cpf}_${index}`,
    cpf: row.cpf ? formatCpf(row.cpf) : "-",
    nome: row.nome || "-",
    /** `isTitular` vem do `IS_EMPLOYEE` gravado; o titular não tem grau de parentesco. */
    grauParentesco: row.isTitular ? "Titular" : row.grauParentesco || "-",
    cpfTitular: row.cpfTitular ? formatCpf(row.cpfTitular) : "-",
    contrato: row.numeroContrato || "-",
    movimentacao: row.tipoMovimentacao || "-",
    nomePlano: row.nomePlano || "-",
    codigoPlano: row.codigoPlano || "-",
    carteirinha: row.carteirinha || "-",
    valorPlano: formatMoney(row.valorPlano),
    matricula: row.matricula || "-",
    sexo: row.sexo || "-",
    estadoCivil: row.estadoCivil || "-",
    dataNascimento: formatDate(row.dataNascimento),
    dataAdmissao: formatDate(row.dataAdmissao),
    vigenciaInicial: formatDate(row.vigenciaInicial),
    vigenciaFinal: formatDate(row.vigenciaFinal),
  }));

  const columns: GridColDef[] = [
    { field: "cpf", headerName: "CPF do Beneficiário", width: 150, sortable: true },
    { field: "nome", headerName: "Nome do Beneficiário", width: 250, sortable: true },
    { field: "grauParentesco", headerName: "Grau de Parentesco", width: 220, sortable: true },
    { field: "cpfTitular", headerName: "CPF do Titular", width: 150, sortable: true },
    { field: "contrato", headerName: "Nº do Contrato", width: 140, sortable: true },
    { field: "movimentacao", headerName: "Movimentação", width: 160, sortable: true },
    { field: "nomePlano", headerName: "Plano", width: 260, sortable: true },
    { field: "codigoPlano", headerName: "Código do Plano", width: 140, sortable: true },
    { field: "carteirinha", headerName: "Carteirinha", width: 160, sortable: true },
    { field: "valorPlano", headerName: "Valor do Plano", width: 130, sortable: true },
    { field: "matricula", headerName: "Matrícula", width: 120, sortable: true },
    { field: "sexo", headerName: "Sexo", width: 130, sortable: true },
    { field: "estadoCivil", headerName: "Estado Civil", width: 140, sortable: true },
    { field: "dataNascimento", headerName: "Nascimento", width: 130, sortable: true },
    { field: "dataAdmissao", headerName: "Admissão", width: 130, sortable: true },
    { field: "vigenciaInicial", headerName: "Início da Vigência", width: 160, sortable: true },
    { field: "vigenciaFinal", headerName: "Fim da Vigência", width: 150, sortable: true },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 10 },
        },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      disableRowSelectionOnClick
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      style={{ backgroundColor: "white", minHeight: 400 }}
      sx={{
        "& .MuiDataGrid-cell": {
          fontSize: "0.875rem",
        },
        "& .MuiDataGrid-columnHeader": {
          fontSize: "0.875rem",
          fontWeight: 600,
        },
      }}
    />
  );
}
